import React, { useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import AdminUserAccountService from './AdminUserAccountService';
import CustomerAddressBook from './CustomerAddressBook';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    zIndex: 1300,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(760px, 96vw)',
    maxHeight: 'min(88vh, 820px)',
    padding: 0,
    border: 'none',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
  },
};

// Unified platform-customer management (P-ADMIN-1 addresses + A-DEAD-1/3 account
// recovery). Both sections shown at once in one scrollable modal. The account
// section is superuser-only; a regular admin sees only the address book. No
// password field by design — admin never handles the customer's password.
const CustomerManageModal = ({ isOpen, onClose, customerId, currentEmail, isActive, isSuperuser, onChanged }) => {
  const [email, setEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const handleChangeEmail = async () => {
    const next = email.trim();
    if (!next) {
      toast.warn('กรุณากรอกอีเมลใหม่');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await AdminUserAccountService.changeEmail(customerId, next);
      toast.success(res.data?.message || 'เปลี่ยนอีเมลเรียบร้อยแล้ว');
      setEmail('');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'เปลี่ยนอีเมลไม่สำเร็จ');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleToggleActive = async () => {
    const next = !isActive;
    if (!next && !window.confirm('ระงับบัญชีนี้? ลูกค้าจะเข้าสู่ระบบไม่ได้จนกว่าจะเปิดใช้งานอีกครั้ง')) {
      return;
    }
    setSavingStatus(true);
    try {
      await AdminUserAccountService.setActive(customerId, next);
      toast.success(next ? 'เปิดใช้งานบัญชีแล้ว' : 'ระงับบัญชีแล้ว');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'ดำเนินการไม่สำเร็จ');
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      ariaHideApp={false}
      contentLabel="ข้อมูลลูกค้าแพลตฟอร์ม"
    >
      <div className="flex max-h-[min(88vh,820px)] flex-col">
        <div className="flex items-center justify-between border-b bg-slate-50 px-5 py-4">
          <h3 className="font-bold text-lg">ข้อมูลลูกค้าแพลตฟอร์ม</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">
            ✕
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-6">
          {/* Account recovery — superuser only */}
          {isSuperuser && (
            <div>
              <h4 className="font-semibold text-base mb-3">บัญชีเข้าสู่ระบบ</h4>

              <label className="block text-sm font-medium text-gray-900 mb-1">อีเมลเข้าสู่ระบบปัจจุบัน</label>
              <div className="text-sm text-gray-600 mb-3 break-all">{currentEmail || '—'}</div>

              <label className="block text-sm font-medium text-gray-900 mb-1">อีเมลใหม่</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="new@email.com"
                  className="flex-1 rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                />
                <button
                  type="button"
                  onClick={handleChangeEmail}
                  disabled={savingEmail}
                  className="px-4 py-1 bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 text-white rounded-md whitespace-nowrap"
                >
                  {savingEmail ? 'กำลังบันทึก…' : 'บันทึกอีเมล'}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                หลังเปลี่ยนอีเมล ลูกค้าใช้ “ลืมรหัสผ่าน” บนอีเมลใหม่เพื่อตั้งรหัสผ่านเอง
              </p>

              <div className="mt-4 flex items-center justify-between border-t pt-3">
                <div>
                  <span className="text-sm font-medium text-gray-900 mr-2">สถานะบัญชี</span>
                  <span
                    className={`px-2 py-0.5 text-sm font-semibold text-white rounded-full ${
                      isActive ? 'bg-green-600' : 'bg-red-600'
                    }`}
                  >
                    {isActive ? 'ใช้งานอยู่' : 'ถูกระงับ'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleToggleActive}
                  disabled={savingStatus}
                  className={`px-4 py-1 disabled:opacity-50 text-white rounded-md ${
                    isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {savingStatus ? 'กำลังดำเนินการ…' : isActive ? 'ระงับบัญชี' : 'เปิดใช้งาน'}
                </button>
              </div>
            </div>
          )}

          {/* Address book */}
          <div className={isSuperuser ? 'border-t pt-5' : ''}>
            <h4 className="font-semibold text-base mb-3">ที่อยู่จัดส่ง</h4>
            {isOpen && customerId && <CustomerAddressBook customerId={customerId} />}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CustomerManageModal;
